/**
 * Safe access to browser globals.
 *
 * Code that runs during render also runs on the server at build time, where
 * `window` and `document` do not exist. Touching them there throws a
 * ReferenceError and fails the build.
 *
 * Note that optional chaining alone does NOT protect you:
 *
 *   window?.foo        // still throws: window is undeclared, not null
 *   browserWindow()?.foo   // safe: returns undefined on the server
 *
 * Use these anywhere a browser global is read during render, including
 * useState initialisers and useEffect dependency arrays, both of which are
 * evaluated while rendering. Code inside useEffect bodies, event handlers and
 * callbacks never runs on the server and does not need them.
 */

/**
 * @returns {boolean} true when running in the browser
 */
export const isBrowser = () => typeof window !== "undefined";

/**
 * @returns {Window | undefined} the window object, or undefined on the server
 */
export const browserWindow = () => (typeof window !== "undefined" ? window : undefined);

/**
 * @returns {Document | undefined} the document object, or undefined on the server
 */
export const browserDocument = () => (typeof document !== "undefined" ? document : undefined);
